import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Users, Plus, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { WhatsAppButton } from "@/components/whatsapp/WhatsAppButton";

type Employee = {
    id: string; position: string; status: string;
    user?: { email: string; full_name: string | null } | null;
    contract_type: string; hire_date: string | null; phone?: string | null;
};

const STATUS_TONE: Record<string, { bg: string; fg: string }> = {
    invited: { bg: "var(--info-bg)", fg: "var(--info-fg)" },
    active: { bg: "var(--success-bg)", fg: "var(--success-fg)" },
    on_leave: { bg: "var(--warning-bg)", fg: "var(--warning-fg)" },
    left: { bg: "var(--neutral-bg)", fg: "var(--neutral-fg)" },
};

export default function Employees() {
    const { t } = useTranslation();
    const [rows, setRows] = useState<Employee[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/hr/employees");
            setRows(r.data.items || []);
        } catch (e: any) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    return (
        <div className="space-y-4" data-testid="hr-employees-page">
            <header className="flex items-center gap-3">
                <Users size={22} style={{ color: "var(--primary)" }} />
                <h1 className="text-xl font-semibold flex-1" style={{ color: "var(--text)" }}>{t("hr.employees.title")}</h1>
            </header>
            <div className="rounded-md p-3 text-xs" style={{ background: "var(--info-bg)", color: "var(--info-fg)" }} data-testid="hr-employees-explain">
                <div className="font-semibold mb-1">{t("hr.employees.explain.title")}</div>
                <div>{t("hr.employees.explain.body")}</div>
            </div>
            {loading && <div className="flex justify-center p-6"><Loader2 className="animate-spin" size={22} /></div>}
            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}
            {!loading && !rows.length && <div className="text-sm" style={{ color: "var(--text-muted)" }}>{t("hr.employees.empty")}</div>}
            {!!rows.length && (
                <div className="overflow-x-auto rounded-xl border" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                    <table className="w-full text-sm">
                        <thead>
                            <tr style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                                <th className="text-left px-3 py-2">{t("hr.employee.name")}</th>
                                <th className="text-left px-3 py-2">{t("hr.employee.position")}</th>
                                <th className="text-left px-3 py-2">{t("hr.employee.contract")}</th>
                                <th className="text-left px-3 py-2">{t("hr.employee.status")}</th>
                                <th className="px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((e) => {
                                const tone = STATUS_TONE[e.status] || STATUS_TONE.left;
                                return (
                                    <tr key={e.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`hr-emp-row-${e.id}`}>
                                        <td className="px-3 py-2" style={{ color: "var(--text)" }}>
                                            <div>{e.user?.full_name || e.user?.email || "—"}</div>
                                            {e.phone && <div className="flex items-center gap-2 text-xs mt-0.5" style={{ color: "var(--text-muted)" }} data-testid={`hr-emp-phone-${e.id}`}>{e.phone}<WhatsAppButton name={e.user?.full_name || e.user?.email || e.position} phone={e.phone} type="employee" id={e.id} testid={`hr-emp-wa-btn-${e.id}`} /></div>}
                                        </td>
                                        <td className="px-3 py-2" style={{ color: "var(--text)" }}>{e.position}</td>
                                        <td className="px-3 py-2" style={{ color: "var(--text-muted)" }}>{String(t([`hr.contract.${e.contract_type}`, e.contract_type]))}</td>
                                        <td className="px-3 py-2">
                                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs" style={{ background: tone.bg, color: tone.fg }} data-testid={`hr-emp-status-${e.id}`}>
                                                {String(t([`hr.status.${e.status}`, e.status]))}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <Link to={`/hr/${e.id}`} className="text-xs font-medium hover:underline" style={{ color: "var(--primary)" }} data-testid={`hr-emp-open-${e.id}`}>{t("common.open")}</Link>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
